import { Grid, Typography, WithStyles } from "@material-ui/core";
import React from "react";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./MyReportsView.styles";
import { useTranslation } from "react-i18next";
import * as SingleIsinReportAction from "../../../../stores/reports/singleIsin/SingleIsinReportAction";
import { useSelector } from "react-redux";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import SingleIsinReportShortModel from "../../../../models/reports/singleIsin/SingleIsinReportShortModel";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { searchCaseInsensitive } from "../../../../utilities/generalUtils";
import { IsinTableComponent } from "./IsinsTableComponent";

export interface IMyReportsViewProps extends WithStyles<typeof styles> {
    onCopyReport?: (date: number, isins: string[]) => void;
    isCopyDisabled: boolean;
}

const MyReportsViewPure: React.FunctionComponent<IMyReportsViewProps> = ({
    onCopyReport,
    isCopyDisabled,
    classes
}: IMyReportsViewProps) => {
    const { t } = useTranslation();

    const reportFromState = useSelector(state => state.singleIsinsReports?.userReports);
    const isRequesting = useSelector(state =>
        selectRequesting(state, [SingleIsinReportAction.REQUEST_USER_REPORTS_FINISHED])
    );
    let reports: IUniversalTableData;

    if (reportFromState) {
        reports = Object.values(reportFromState).map((report: SingleIsinReportShortModel) => ({
            id: report.id,
            date: report.date,
            isins: report.isins,
            created: report.created,
            price: report.price,
            discount: report.discount,
            totalPrice: report.totalPrice,
            state: report.state,
            reportLink: report.reportLink,
            ...(isCopyDisabled && {
                actionsDisabled: ["copy"]
            })
        }));
    }

    const handleCopy = index => {
        const selectedReport = reports[index];
        onCopyReport(
            selectedReport?.date,
            selectedReport?.isins?.map(i => i.isin)
        );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: "Report ID",
            field: "id"
        },
        {
            type: "date",
            title: "Date",
            field: "date"
        },
        {
            type: "component",
            title: "Isins",
            component: rowData => <IsinTableComponent isins={rowData?.isins} state={rowData?.state} />,
            searchFunc: (term, rowData) => rowData?.isins?.some(isin => searchCaseInsensitive(term, isin?.isin))
        },
        {
            type: "dateTime",
            title: t("general.createdOn"),
            field: "created"
        },
        {
            type: "price",
            title: "Subtotal",
            field: "price"
        },
        {
            type: "price",
            title: "Discount Amount",
            field: "discount"
        },
        {
            type: "price",
            title: "Total amount (excl. VAT)",
            field: "totalPrice"
        },
        {
            type: "link",
            title: "Report",
            field: "reportLink",
            linkField: "reportLink",
            linkTitle: "Download",
            fieldIfLinkNotAvailable: "state"
        },
        {
            type: "actions",
            title: "Copy",
            actions: [
                {
                    name: "copy",
                    icon: <FileCopyIcon />,
                    tooltip: "Use parameters from this report",
                    onClick: index => handleCopy(index),
                    disabledTooltip: "Your subscription is not active"
                }
            ]
        }
    ];

    return (
        <Grid container className={classes.disclaimerBox}>
            <Grid item xs={12}>
                <UniversalTable columns={columns} data={reports} isLoading={isRequesting} searchable />
            </Grid>
            <Grid item xs={12}  >
                <Typography variant="subtitle2" className={classes.disclaimerText} >
                    Disclaimer: The Geographical Holdings Report is a snapshot of Clearstream holdings taken at
                    approximately 20:25 CET. In addition, the report is anonymised and censored in line with our
                    regulatory and governance requirements. Values reported may therefore not match holdings reported
                    elsewhere.
                </Typography>
            </Grid>
        </Grid>
    );
};

export const MyReportsView = withStyles(styles)(MyReportsViewPure);
